/**
 * S4 (AUDIT-INCIDENT-HISTORY PRD v1) — small RFC-4180 CSV util used by the
 * audit/incident table exports. `toCsv` is pure (returns the string, BOM
 * included); `downloadCsv` performs the browser-only download.
 */

export interface CsvColumn<Row> {
  key: keyof Row & string;
  label: string;
}

/** Escape one cell per RFC-4180: quote fields containing comma/quote/CR/LF; double embedded quotes. */
function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Build a CSV document: UTF-8 BOM (Excel), header row from the column labels,
 * CRLF line endings, RFC-4180 quoting. Missing/null cells render empty —
 * never the string "null".
 */
export function toCsv<Row extends object>(columns: Array<CsvColumn<Row>>, rows: Row[]): string {
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const body = rows.map((row) => columns.map((c) => escapeCell(row[c.key])).join(','));
  return `\uFEFF${[header, ...body].join('\r\n')}\r\n`;
}

/** Browser-only download of a CSV string (anchor click + revoke). */
export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
